// ROOM 404: Dynamic Rotating QR Code Renderer
// Self-contained micro matrix generator with live 45s countdown timer bar and refresh trigger.

import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, Shield } from 'lucide-react';

interface QrCodeProps {
  token: string;
  expiresAt: number;
  ttlSeconds: number;
  onRefresh: () => void;
}

export const QrCodeRenderer: React.FC<QrCodeProps> = ({
  token,
  expiresAt,
  ttlSeconds,
  onRefresh,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [secondsLeft, setSecondsLeft] = useState<number>(ttlSeconds);

  // Live countdown timer
  useEffect(() => {
    const updateCountdown = () => {
      const nowSec = Math.floor(Date.now() / 1000);
      const remaining = Math.max(0, expiresAt - nowSec);
      setSecondsLeft(remaining);

      if (remaining <= 0) {
        onRefresh();
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, onRefresh]);

  // Deterministic 2D visual matrix rendering from token hash
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const size = 220;
    canvas.width = size;
    canvas.height = size;

    // Background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, size, size);

    const gridSize = 25; // 25x25 QR grid
    const cellSize = size / gridSize;

    // Simple deterministic pseudo-random hash generator from token
    let seed = 0;
    for (let i = 0; i < token.length; i++) {
      seed = (seed * 31 + token.charCodeAt(i)) >>> 0;
    }
    const pseudoRandom = () => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 4294967296;
    };

    ctx.fillStyle = '#090a0f';

    // 1. Draw 3 Corner Position Markers
    const drawFinderPattern = (startX: number, startY: number) => {
      // 7x7 outer square
      ctx.fillRect(startX * cellSize, startY * cellSize, 7 * cellSize, 7 * cellSize);
      // 5x5 white inner
      ctx.fillStyle = '#ffffff';
      ctx.fillRect((startX + 1) * cellSize, (startY + 1) * cellSize, 5 * cellSize, 5 * cellSize);
      // 3x3 black center
      ctx.fillStyle = '#090a0f';
      ctx.fillRect((startX + 2) * cellSize, (startY + 2) * cellSize, 3 * cellSize, 3 * cellSize);
    };

    drawFinderPattern(1, 1);
    drawFinderPattern(gridSize - 8, 1);
    drawFinderPattern(1, gridSize - 8);

    // 2. Draw Data Cells
    ctx.fillStyle = '#090a0f';
    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        // Skip finder areas
        if (
          (r < 9 && c < 9) ||
          (r < 9 && c >= gridSize - 9) ||
          (r >= gridSize - 9 && c < 9)
        ) {
          continue;
        }

        // Timing patterns
        if (r === 6 || c === 6) {
          if ((r + c) % 2 === 0) {
            ctx.fillRect(c * cellSize, r * cellSize, cellSize, cellSize);
          }
          continue;
        }

        if (pseudoRandom() > 0.52) {
          ctx.fillRect(c * cellSize, r * cellSize, cellSize, cellSize);
        }
      }
    }

    // 3. Central Brand Shield Insignia
    const centerSize = 34;
    const centerPos = (size - centerSize) / 2;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(centerPos, centerPos, centerSize, centerSize, 6);
    ctx.fill();

    ctx.fillStyle = '#10b981';
    ctx.beginPath();
    ctx.roundRect(centerPos + 3, centerPos + 3, centerSize - 6, centerSize - 6, 4);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 11px JetBrains Mono';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('404', size / 2, size / 2);
  }, [token]);

  const percentage = Math.max(0, Math.min(100, (secondsLeft / ttlSeconds) * 100));

  return (
    <div className="flex flex-col items-center">
      {/* Canvas container */}
      <div className="relative p-3.5 bg-white rounded-2xl shadow-xl shadow-emerald-500/5">
        <canvas ref={canvasRef} className="rounded-lg block" />
      </div>

      {/* Countdown Progress Bar */}
      <div className="w-full max-w-[220px] mt-3">
        <div className="flex items-center justify-between text-[11px] text-gray-400 font-mono mb-1">
          <span className="flex items-center space-x-1">
            <Shield className="w-3 h-3 text-emerald-400" />
            <span>Rotating Token</span>
          </span>
          <span className="font-semibold text-emerald-400">{secondsLeft}s</span>
        </div>
        <div className="w-full bg-[#1f2433] h-1.5 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-1000 ${
              secondsLeft < 10 ? 'bg-red-500' : 'bg-emerald-400'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Manual refresh button */}
      <button
        onClick={onRefresh}
        className="mt-2 text-xs text-gray-400 hover:text-emerald-400 flex items-center space-x-1 transition-colors"
      >
        <RefreshCw className="w-3 h-3" />
        <span>Rotate now</span>
      </button>
    </div>
  );
};
